/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines */

// deps
import React, { memo, useCallback, useContext, useRef, useEffect, useMemo, useState } from "react";
import classNames from "classnames";

// internal-deps
import {
    ElementObserver,
    GenericError,
    isEmpty,
    useEffectOnce,
    clearTimerFromStorage,
    isNetworkError,
    LocalStorage,
    LOCAL_STORAGE_KEYS,
    format,
    scrollElementIntoViewWithOffset,
    Source,
    RouteProps,
    isMWebPlatform,
} from "@swiggy-private/core-ui";
import {
    CouponModalWrapper,
    EditNumberWidget,
    DelightPopup,
    IrctcAddressChange,
    CART_STATUS_CODES,
    CART_GENERIC_EVENT_CASES,
    GenericCartToast,
    fullCaseToTitleCase,
    BharatMandapamConfirmPickup,
} from "@swiggy-private/cart-ui";
import {
    CancellationPolicyRef,
    CouponConfetti,
    ValueDrivenCrossSellXP,
    TDSuccessCardType,
    IrctcAddressChangeFormKeys,
    UserAddress,
} from "@swiggy-private/cart-types";
import Blaze from "@swiggy-private/blaze.js";
import { NavigationScreenLinkTypes, NavigationTypes } from "@swiggy-private/native-bridge";
import {
    useNavigate,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    AnalyticsContext,
    CheckoutContext,
    CheckoutActionTypes,
    CheckoutRoute,
    getBridgeSdk,
    UserContext,
    recordNREvent,
    recordGenericErrorNREvent,
    getUserFromUserContextValue,
    VisualEffectContext,
    closeWebview,
    closeIxigoWebview,
    NEWRELIC_TABLES,
    useSessionStorage,
    BLAZE_EVENTS,
    GroupOrderCartContext,
    isPopFlow,
    isIxigoFlow,
    CouponPageType,
    isJioHotstarFlow,
} from "@swiggy-private/checkout-core";

//styles
import styles from "./CartRenderer.scss";

// components
import { CartFooter } from "@cart-client/components/cart-footer";
import { UpdateProgressLoader } from "@swiggy-private/core-ui";
import { Loader } from "@swiggy-private/dls-web-core/dist/components/Loader";
import CartUIWrapper from "./cart-ui-wrapper";
import CartError from "@cart-client/components/cart-error";
import { CartTimerWrapper } from "@cart-client/components/cart-timer-wrapper";
import { Shimmer } from "@cart-client/components/shimmer";
import { CartWidgetRenderer } from "@cart-client/components/cart-widget-renderer";
import { SwiggyPlanBullsEyeTGWrapper } from "@cart-client/components/swiggy-plan-bulls-eye-tg-wrapper";
import { AddressListManager } from "@cart-client/components/address-list-manager";
import { BlackMembershipNudgesWrapper } from "@cart-client/components/black-membership-nudges-wrapper";
import GenericCroutonWrapper from "@cart-client/components/generic-crouton-wrapper/GenericCroutonWrapper";
import M2CHalfcardContainer from "@cart-client/components/swiggy-plan-bulls-eye-tg-wrapper/M2CHalfcardContainer";
import FreebiePopup from "../freebie-widget-renderer/components/FreebiePopup";
import { SplashLottie } from "@cart-client/components/splash-lottie";
import { CancellationPolicyWidget } from "@swiggy-private/core-ui";

// constants
import { LOGIN, RETRY, SOCIAL_OFO_TEXT } from "@cart-client/constants/TextDict";
import {
    FLOW_TYPE_OFO,
    HTTP_STATUS_CODES,
    ERROR_SCREEN_RETRY_WAIT_TIME_SECS,
    IRCTC_ADDRESS_CHANGE_DEEPLINK,
} from "@cart-client/constants/index";
import { DEFAULT_PAYMENTS_RESPONSE_KEY, IS_IRCTC_CART_SESSION_KEY } from "@cart-client/constants/StorageKeys";
import { TD_COUPON_MODAL } from "@cart-client/constants/Coupon";
import { confettiMapping } from "@cart-client/constants/Coupon";
import {
    CART_ELEMENT_IDS,
    SCROLL_MARGIN_TOP_WITH_SAVING_BANNER,
    SCROLL_MARGIN_TOP_WITHOUT_BANNER,
} from "@cart-client/components/cart-renderer/constants";

// helpers
import {
    getMenuItems,
    getCouponErrorCard,
    isCartEmpty,
    getUnServiceableItemsCount,
    getCartItemsCount,
    getRestaurantName,
    getRestaurantId,
    getTipAmount,
    getSavingsBannerData,
    getIsAwayLocation,
    getOrderFlowType,
    getIrctcIdentifier,
    getIrctcTrainOrderAttributes,
    getOFOWidgetDetails,
    extractOFOAddressFromEditAddressNB,
    getQFoodSLA,
    getCartId,
    getTradeDiscountCouponCardUI,
    getIrctcAddressChangeUrl,
    handleAddMoreItemsClick,
    isPORV2Flow,
    getFreebiePopupInfo,
    hasCartSplashLottie,
} from "@cart-client/helpers/Cart";
import { isBulkOrder } from "@cart-client/helpers/BulkOrder";
import { isClosingTimeAvailable, setClosingTimeInStorage } from "@cart-client/helpers/TimerOnCart";
import { isTurboPaymentEnabled } from "@cart-client/helpers/TurboPayment";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";
import { syncCartAddressHeaders } from "@cart-client/helpers/Header";
import { isUserLoggedIn } from "@cart-client/helpers/User";
import { logCartViewedEvent } from "@cart-client/helpers/Clevertap";
import { openEditNumberBottomSheet } from "@cart-client/helpers/OFO";
import { recordCartPercievedTime } from "@cart-client/helpers/Metric";
import { isCartTotalZero, isItemExceededLimitMessageExists, isOnTimeCart } from "@cart-client/helpers/CartItemRenderer";
import { isMovieBeamContextExists } from "@cart-client/helpers/CartContext";
import { isBharatMandapamCity } from "@cart-client/helpers/BharatMandapam";

// hooks
import useFetchCart from "@cart-client/hooks/useFetchCart";
import { useCartToast } from "@cart-client/hooks/useCartToast";
import { useFetchAddressOnClick } from "@cart-client/hooks/useFetchAddressOnClick";
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";
import { useCrossSellAnalytics } from "@cart-client/hooks/useCrossSellAnalytics";
import { useSwiggyPlanBullsEyeTG } from "@cart-client/hooks/useSwiggyPlanBullsEyeTG";
import { useIPLSwiggySixesCartCroutonAndUpdates } from "@cart-client/hooks/useIPLSwiggySixesCartCroutonAndUpdates";
import { useIPLSwiggySixesTimerCrouton } from "@cart-client/hooks/useIPLSwiggySixesTimerCrouton";
import { usePrefetchlottie } from "@cart-client/hooks/usePrefetchLottie";
import { useGenericCrouton } from "@cart-client/hooks/useGenericCrouton";
import useCartLoadImpression from "@cart-client/hooks/useCartLoadImpression";

// reducers
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartToastContext } from "@cart-client/contexts/CartToastContext";
import { CouponListingContext } from "@cart-client/contexts/CouponListingContext";

//analytics
import {
    clickProceedToPay,
    clickViewDetailedBill,
    clickAddMoreItemsCta,
    onWebcartUpdateImpression,
    clickOcassionsProceedToPayCTA,
    impressionCartLoad,
} from "@cart-client/analytics/events";
import {
    clickAddDetails,
    clickEditDetails,
    impressionAddDetails,
    impressionEditDetails,
} from "@cart-client/analytics/events/OFO";
import useAutoApplyCallout from "@cart-client/hooks/useAutoApplyCallout";
import { impressionCancellationPolicyWidget } from "@cart-client/analytics/events";

// interfaces
import { CtaAction, OFOWidgetPosition } from "@cart-client/interfaces/Cart";
import { updateStatusBarCleanup } from "@cart-client/helpers/IncognitoCartHelper";
import { CLEVERTAP_KEYS } from "@cart-client/constants/Clevertap";
import { handleEmptyCartOnLoad } from "@cart-client/helpers/EmptyCart";

function CartRenderer(_props: RouteProps<CheckoutRoute>): React.ReactElement {
    const [intialCartLoaded, setInitalCartLoaded] = useState(false);
    const {
        loading,
        localCartState,
        reviewedCartState,
        error: cartApiError,
        backgroundRefreshInProgress,
    } = useFetchCart();
    const { cartState, setCartState } = useContext(CartContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);
    const { resetCouponListingPage } = useContext(CouponListingContext);
    const { showConfetti } = useContext(VisualEffectContext);
    const { user: userContextValue } = useContext(UserContext);
    const { state: groupCartState, isGroupCartFlow } = useContext(GroupOrderCartContext);
    const user = useMemo(() => getUserFromUserContextValue(userContextValue), [userContextValue]);

    const cartLoadMetadata = useCartLoadImpression();

    const [isTurboPayWidgetHidden, setIsTurboPayWidgetHidden] = useState(false);

    const { reviewedCart: cartResponse, localCart } = cartState;
    const { cartFeedbackXP, valueDrivenCrossSellXPVariant, porFlowXP } = useCartRendererXP(cartResponse);

    const planBullsEyeTGProps = useSwiggyPlanBullsEyeTG(cartResponse);
    const isPopCart = isPopFlow();
    const isIrctcCart = useMemo(() => !isEmpty(getIrctcIdentifier(cartResponse)), [cartResponse]);

    const showCartSplashLottie = useMemo(() => hasCartSplashLottie(cartResponse), [cartResponse]);

    const [isIrctcCartSession, setIrctcCartSession] = useSessionStorage(
        IS_IRCTC_CART_SESSION_KEY,
        !!getIrctcIdentifier(cartResponse),
    );
    const [forceExtraPaddingOnCart, setForceExtraPaddingOnCart] = useState(false);

    const [showBharatMandapamConfirm, setShowBharatMandapamConfirm] = useState(false);

    const {
        impressionAutoApplyCouponPopup,
        impressionOffercashSuccessPopup,
        impressionDelightPopup,
        onClickCloseDelightPopup,
        onTimerLoad,
        onClickCouponSavingsPopup,
        onClickCouponSavingsPopupWithPOR,
        onImpressionIrctcUpdateAddress,
        onClickIrctcEditCta,
        onImpressionMoreOffersStrip,
        onClickMoreOffersStrip,
        onClickCouponMaxSavingsPopup,
        onImpressionSplashLottie,
    } = useCartRendererAnalytics(cartResponse);
    usePrefetchlottie();

    const { onClickAddMoreAndSaveMore } = useCrossSellAnalytics(cartResponse);

    const [isCroutonVisible, setIsCroutonVisible] = useState(false);

    const setCroutonVisibility = useCallback((croutonVisibility: boolean) => {
        setIsCroutonVisible(croutonVisibility);
    }, []);

    useEffect(() => {
        setIsTurboPayWidgetHidden(false);
    }, [cartState.uniqueIdForCart]);

    const { croutonData, allowIPLCroutonOverRestaurantTimer } = useIPLSwiggySixesCartCroutonAndUpdates(cartResponse);
    // Only show timer if servecieable, bcz backend always sends the time even in error cases.
    // special handling for IPL crouton case where it might take priority when ipl timer < restaurant timer
    const canShowTimer =
        cartResponse?.statusCode === 0 && isClosingTimeAvailable(cartResponse) && !allowIPLCroutonOverRestaurantTimer;

    useIPLSwiggySixesTimerCrouton({ croutonData, setCroutonVisibility });
    useGenericCrouton({ setCroutonVisibility, canShowTimer });

    const {
        updateCartInProgress,
        updateCartError,
        doUpdateCart,
        reloadCart,
        showCouponModal,
        toggleCouponModal,
        showM2ChalfcardOnClick,
        showPendingDiscountingComms,
        showFreebiePopup,
        setShowFreebiePopup,
    } = useContext(UpdateCartContext);

    const showCartToast = useCartToast(cartResponse, isCroutonVisible);
    const { cartToastData } = useContext(CartToastContext);
    const {
        showDelightPopup,
        couponSuccessData,
        isCouponAutoApplied,
        tradeDiscountSuccessInfo,
        handleCloseDelightPopup,
    } = useAutoApplyCallout(cartResponse, toggleCouponModal);

    const isCartStatusSuccess = useMemo(
        () => cartResponse?.statusCode === CART_STATUS_CODES.SUCCESS,
        [cartResponse?.statusCode],
    );

    const { displayAddressList, goToSelectAddress, selectedAddress } = useFetchAddressOnClick(
        cartResponse?.data?.cart_total,
        cartResponse?.data?.addresses,
        isCartStatusSuccess,
    );

    const userLoggedIn = isUserLoggedIn();
    const isBharatMandapam = useMemo(() => isBharatMandapamCity() && userLoggedIn, [userLoggedIn]);

    const BHARAT_MANDAPAM_CONFIRMED_KEY = "bharat_mandapam_confirmed";

    const isBharatMandapamConfirmShown = useCallback(() => {
        const sid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "0";
        return LocalStorage.getItem(BHARAT_MANDAPAM_CONFIRMED_KEY) === sid;
    }, []);

    const markBharatMandapamConfirmShown = useCallback(() => {
        const sid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "0";
        LocalStorage.setItem(BHARAT_MANDAPAM_CONFIRMED_KEY, sid);
    }, []);

    // Show Bharat Mandapam confirm half card once per sid
    useEffect(() => {
        if (!isBharatMandapam || !userLoggedIn) return;

        if (updateCartInProgress) return;

        if (!isBharatMandapamConfirmShown()) {
            setShowBharatMandapamConfirm(true);
        }
    }, [isBharatMandapam, isBharatMandapamConfirmShown, userLoggedIn, updateCartInProgress]);

    const handleCloseBharatMandapamConfirm = useCallback(() => {
        markBharatMandapamConfirmShown();
        setShowBharatMandapamConfirm(false);
    }, [markBharatMandapamConfirmShown]);

    const handleBharatMandapamChangePickup = useCallback(() => {
        markBharatMandapamConfirmShown();
        setShowBharatMandapamConfirm(false);
        goToSelectAddress();
    }, [goToSelectAddress, markBharatMandapamConfirmShown]);

    const qFoodSLA = useMemo(() => getQFoodSLA(cartResponse), [cartResponse]);

    const tradeDiscountCouponInfo = getTradeDiscountCouponCardUI(cartResponse);
    const freebiePopupInfo = getFreebiePopupInfo(isPopCart ? cartResponse : null);

    const cancellationPolicyRef = useRef<CancellationPolicyRef>();
    const makePaymentButtonRef = useRef<HTMLDivElement>(null);
    const planAddedRef = useRef<HTMLDivElement>(null);
    const isFromBottomBar = GlobalHeaderState.getHeader(SwgyHeaderKeysList.isFromBottomBar) === "true";
    const sessionId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "0";
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
    const isMweb = isMWebPlatform(platform);
    const showCouponConfetti = LocalStorage.getItem(LOCAL_STORAGE_KEYS.COUPON_CONFETTI_SHOWN) !== sessionId;
    const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";

    const isValueMeal = tradeDiscountCouponInfo?.td_success_card_type === TDSuccessCardType.OFFER_CARD_TYPE_VALUE_MEALS;
    const disableRedirectionToMenu = isValueMeal;
    const tDCouponModal = LocalStorage.getItem<{ [key: string]: string }>(TD_COUPON_MODAL.TD_COUPON_MODAL);
    const [showTradeDiscountCoupon, setShowTradeDiscountCoupon] = useState(false);
    const onClickTDCouponModal = useCallback(() => {
        setShowTradeDiscountCoupon(false);
        LocalStorage.setItem(TD_COUPON_MODAL.TD_COUPON_MODAL, {
            restaurantId: getRestaurantId(cartResponse),
            savings: tradeDiscountCouponInfo?.savings || "",
        });
    }, [cartResponse, tradeDiscountCouponInfo?.savings]);

    useEffect(() => {
        if (
            !isEmpty(tradeDiscountCouponInfo) &&
            (tDCouponModal?.restaurantId !== getRestaurantId(cartResponse) ||
                tDCouponModal?.savings !== tradeDiscountCouponInfo?.savings)
        )
            setShowTradeDiscountCoupon(true);
    }, [cartResponse, tDCouponModal, tradeDiscountCouponInfo]);

    const savingsBannerData = getSavingsBannerData(cartResponse);

    const canShowFooterCrouton = useMemo(
        () => !canShowTimer && !isItemExceededLimitMessageExists(cartResponse),
        [canShowTimer, cartResponse],
    );

    const showOFOWidget = useMemo(
        () => getOrderFlowType(cartResponse) === FLOW_TYPE_OFO || getIsAwayLocation(cartResponse),
        [cartResponse],
    );

    const showIrctcCart = useMemo(() => !isEmpty(getIrctcIdentifier(cartResponse)), [cartResponse]);

    const [isPageScrolled, setPageScrolled] = useState(false);
    const deliverySlots = cartResponse?.data?.slot_details;

    useEffect(() => {
        // set closing time in storage
        setClosingTimeInStorage(cartResponse);
    }, [cartResponse]);

    const onTimerEnd = useCallback(async () => {
        clearTimerFromStorage();
        reloadCart();
        recordNREvent({ eventType: NEWRELIC_TABLES.TIMER_END_UPDATE_CART_CALLED });
    }, [reloadCart]);

    const navigateTo = useNavigate();

    // If cart total is 0, we will direclty place the order from cart itself
    const canPlaceOrderFromCart = useMemo(() => isCartTotalZero(cartResponse), [cartResponse]);

    const turboPaymentEnabled = useMemo(() => {
        return isTurboPaymentEnabled(cartResponse, isTurboPayWidgetHidden);
    }, [cartResponse, isTurboPayWidgetHidden]);

    const handleCartModalClose = useCallback(() => {
        toggleCouponModal();
        if (showCouponModal) {
            showPendingDiscountingComms();
        }
    }, [toggleCouponModal, showCouponModal, showPendingDiscountingComms]);

    const gotoPayment = useCallback(() => {
        // log payment start intreaction time, any change here should be reflected in `packages/payment-client/src/components/payment-main/PaymentMain.tsx` as well
        Blaze.recordStart(BLAZE_EVENTS.RECORD_CHECKOUT_PAYMENT_LOAD_TIME);

        // clear cached payment data before goiing to payment screen from web cart
        // We also clear cache data if going to payment page from `Change` cta on turbo widget
        if (isSessionStorageAvailable) {
            sessionStorage.removeItem(DEFAULT_PAYMENTS_RESPONSE_KEY);
            sessionStorage.removeItem("with_coupon");
            sessionStorage.setItem("isFromWebCart", "true");
        }

        if (isFromBottomBar) {
            getBridgeSdk().toggleNativeBottombar({ show: false });
        }

        setCheckoutState({
            type: CheckoutActionTypes.SHOW_PAYMENTS,
            payload: { showIrctcCart },
        });
        navigateTo(CheckoutRoute.PAYMENTS, {}, false);
    }, [
        cartResponse?.data?.cart_total,
        isFromBottomBar,
        isSessionStorageAvailable,
        navigateTo,
        setCheckoutState,
        showIrctcCart,
    ]);

    const placeOrderFromCart = useCallback(() => {
        // clear cached payment data before goiing to payment screen from web cart
        if (isSessionStorageAvailable) {
            sessionStorage.removeItem(DEFAULT_PAYMENTS_RESPONSE_KEY);
            sessionStorage.removeItem("with_coupon");
            sessionStorage.setItem("isFromWebCart", "true");
        }

        if (isFromBottomBar) {
            getBridgeSdk().toggleNativeBottombar({ show: false });
        }
        const cartTotal = cartResponse?.data?.cart_total;
        setCheckoutState({
            type: CheckoutActionTypes.DIRECT_PLACE_ORDER,
            payload: { fromCart: true, cartTotal: cartTotal || 0, orderComment: localCart?.orderComment || "" },
        });
    }, [isSessionStorageAvailable, isFromBottomBar, setCheckoutState, cartResponse, localCart]);

    const viewDetailedBill = useCallback(() => {
        clickViewDetailedBill(cartResponse?.data?.cart_total ?? 0, clickEvent);
        scrollElementIntoViewWithOffset(
            CART_ELEMENT_IDS.BILL_DETAILS_CONTAINER,
            isEmpty(savingsBannerData) ? SCROLL_MARGIN_TOP_WITHOUT_BANNER : SCROLL_MARGIN_TOP_WITH_SAVING_BANNER,
            true,
            CART_ELEMENT_IDS.CART_RENDERER_CONTAINER_SELECTOR,
        );
    }, [cartResponse, clickEvent, savingsBannerData]);

    // Footer action can be either go to payment screen or directly place order from cart in case of 0 amount to pay
    const cartFooterAction = useMemo(
        () => (canPlaceOrderFromCart ? placeOrderFromCart : gotoPayment),
        [canPlaceOrderFromCart, gotoPayment, placeOrderFromCart],
    );

    const isBulkOrderFn = useMemo(() => {
        return isBulkOrder(cartResponse);
    }, [cartResponse]);

    const onMakePayment = useCallback(
        (selectedSlot?: string) => {
            // analytics event
            if (!isEmpty(deliverySlots)) {
                if (isBulkOrderFn) {
                    const context = {
                        cartId: cartResponse?.data?.cartId,
                        bulkOrder: true,
                        cartAmount: cartResponse?.data?.cart_total ?? 0,
                        selectedSlot: selectedSlot,
                    };
                    clickOcassionsProceedToPayCTA(
                        clickEvent,
                        cartResponse?.data?.restaurant_details?.id,
                        JSON.stringify(context),
                    );
                } else {
                    const slotContext = {
                        cartId: cartResponse?.data?.cartId,
                        slotSelected: deliverySlots.slots.filter((slot) => slot.available === true)[0],
                    };
                    clickProceedToPay(clickEvent, {
                        cartAmount: cartResponse?.data?.cart_total ?? 0,
                        restaurantId: cartResponse?.data?.restaurant_details?.id,
                        context: JSON.stringify(slotContext),
                        groupCartId: groupCartState?.data?.group_cart_id,
                    });
                }
            } else {
                clickProceedToPay(clickEvent, {
                    cartAmount: cartResponse?.data?.cart_total ?? 0,
                    restaurantId: cartResponse?.data?.restaurant_details?.id,
                    groupCartId: groupCartState?.data?.group_cart_id,
                });
            }

            // sync client headers before navigating to payments
            // update cartAddressId && cartId

            const cartId = cartResponse?.data?.cartId || "";
            const addressId = cartResponse?.data?.address_id || "";

            syncCartAddressHeaders({ cartId, addressId });

            // show cancellation popup if required
            if (cancellationPolicyRef && cancellationPolicyRef.current) {
                cancellationPolicyRef.current.interceptPayment();
            } else {
                cartFooterAction();
            }
        },
        [
            deliverySlots,
            cartResponse?.data?.cartId,
            cartResponse?.data?.address_id,
            cartResponse?.data?.cart_total,
            cartResponse?.data?.restaurant_details?.id,
            isBulkOrderFn,
            clickEvent,
            groupCartState?.data?.group_cart_id,
            cartFooterAction,
        ],
    );

    useEffect(() => {
        if (!loading && reviewedCartState != null && intialCartLoaded === false) {
            const payload = {
                uniqueIdForCart: randomStringIdGenerator(),
                localCart: localCartState,
                reviewedCart: reviewedCartState,
            };

            // log clevertap event for cart viewed
            logCartViewedEvent(reviewedCartState);

            recordCartPercievedTime();

            impressionCartLoad({
                impressionEvent,
                metadata: cartLoadMetadata,
                cartId: reviewedCartState?.data?.cartId,
                restaurantId: reviewedCartState?.data?.restaurant_details?.id,
                groupCartId: groupCartState?.data?.group_cart_id,
            });

            // update cart state
            setCartState({ type: CartActionTypes.UPDATE_CART, payload });
            setInitalCartLoaded(true);
        }
        if (showIrctcCart) {
            getBridgeSdk().logClevertapEvent({ eventName: CLEVERTAP_KEYS.IRCTC_CART_VISIT, params: {} });
            // appsflyer events
            getBridgeSdk().logEvent({ eventName: CLEVERTAP_KEYS.IRCTC_CART_VISIT, eventParams: {} });
        }
    }, [
        localCartState,
        reviewedCartState,
        loading,
        setCartState,
        user,
        intialCartLoaded,
        showIrctcCart,
        impressionEvent,
        cartLoadMetadata,
        groupCartState?.data?.group_cart_id,
    ]);

    useEffectOnce(() => {
        if (isFromBottomBar) {
            getBridgeSdk().toggleNativeBottombar({
                show: true,
            });
        }
    });

    const goToHome = (): void => {
        // For Ixigo flow, close webview directly instead of navigating
        if (isIxigoFlow()) {
            closeIxigoWebview();
            return;
        }

        getBridgeSdk().navigateToScreen({
            type: NavigationTypes.SCREEN,
            link: NavigationScreenLinkTypes.HOME,
        });
    };

    const headerSelectedAddress: UserAddress | null | undefined = useMemo(() => {
        const irctcData = getIrctcIdentifier(cartResponse);
        const trainOrderAttributes = getIrctcTrainOrderAttributes(cartResponse);
        if (isIrctcCart) {
            if (!irctcData || !trainOrderAttributes) {
                return undefined;
            }
            return {
                annotation: `${irctcData.coach}/${irctcData.berth}, ${fullCaseToTitleCase(trainOrderAttributes.train_name)}`,
                address: "",
            } as UserAddress;
        }
        return selectedAddress;
    }, [isIrctcCart, selectedAddress, cartResponse]);

    const onImpressionTooManyRequestsErrorPage = useCallback(
        () => recordGenericErrorNREvent({ errorContext: CART_GENERIC_EVENT_CASES.TOO_MANY_REQUESTS }),
        [],
    );
    const onImpressionNullCartResponseErrorPage = useCallback(
        () => recordGenericErrorNREvent({ errorContext: CART_GENERIC_EVENT_CASES.NULL_CART_RESPONSE }),
        [],
    );

    const goToMenu = useCallback(() => {
        const cartId = String(cartResponse?.data?.cartId ?? "");
        if (valueDrivenCrossSellXPVariant === ValueDrivenCrossSellXP.ADD_MORE_ITEMS_TEXT) {
            onClickAddMoreAndSaveMore(false);
        } else {
            clickAddMoreItemsCta(clickEvent, cartId);
        }
        updateStatusBarCleanup();
        if (isPopCart) {
            goToHome();
        } else {
            handleAddMoreItemsClick({ isGroupCartFlow, isFromBottomBar, groupCartState, cartResponse });
        }
    }, [
        cartResponse,
        clickEvent,
        groupCartState,
        isFromBottomBar,
        isGroupCartFlow,
        isPopCart,
        onClickAddMoreAndSaveMore,
        valueDrivenCrossSellXPVariant,
    ]);

    const showAddressWidgetTop = useMemo(() => userLoggedIn && selectedAddress, [userLoggedIn, selectedAddress]);

    const triggerCouponConfetti = useCallback(
        (type: CouponConfetti) => {
            switch (type) {
                case CouponConfetti.TOP_UP:
                    showConfetti(confettiMapping.CONFETTI_TYPE_TOP_UP);
                    LocalStorage.setItem(LOCAL_STORAGE_KEYS.COUPON_CONFETTI_SHOWN, sessionId);
                    break;
                case CouponConfetti.AUTO_APPLY:
                    showConfetti(confettiMapping.CONFETTI_TYPE_AUTO_APPLY);
                    LocalStorage.setItem(LOCAL_STORAGE_KEYS.COUPON_CONFETTI_SHOWN, sessionId);
                    break;
                case CouponConfetti.INVALID:
                default:
                    break;
            }
        },
        [showConfetti, sessionId],
    );

    const { titleOFOWidget, subTitleOFOWidget, actionTextOFOWidget, showGiftIcon, widgetPosition } = useMemo(
        () => getOFOWidgetDetails(selectedAddress, getOrderFlowType(cartResponse), getIsAwayLocation(cartResponse)),
        [selectedAddress, cartResponse],
    );

    const canShowOFOWidgetAtTop = useMemo(
        () => showOFOWidget && !!titleOFOWidget && widgetPosition !== OFOWidgetPosition.BOTTOM,
        [showOFOWidget, titleOFOWidget, widgetPosition],
    );

    const onClickOFOWidgetEvent = useCallback(() => {
        if (actionTextOFOWidget === SOCIAL_OFO_TEXT.editAction) {
            clickEditDetails(cartResponse?.data?.cartId || "", !!localCartState?.userLiveLat, clickEvent);
        } else {
            clickAddDetails(cartResponse?.data?.cartId || "", !!localCartState?.userLiveLat, false, clickEvent);
        }
    }, [actionTextOFOWidget, cartResponse, localCartState, clickEvent]);

    const onImpressionOFOWidgetEvent = useCallback(() => {
        if (actionTextOFOWidget === SOCIAL_OFO_TEXT.editAction) {
            impressionEditDetails(cartResponse?.data?.cartId || "", !!localCartState?.userLiveLat, impressionEvent);
        } else {
            impressionAddDetails(
                cartResponse?.data?.cartId || "",
                !!localCartState?.userLiveLat,
                false,
                impressionEvent,
            );
        }
    }, [actionTextOFOWidget, cartResponse, localCartState, impressionEvent]);

    const onOFOWidgetActionClick = useCallback(() => {
        onClickOFOWidgetEvent();
        selectedAddress &&
            openEditNumberBottomSheet(selectedAddress, actionTextOFOWidget).then((response) => {
                const parsedAddress = extractOFOAddressFromEditAddressNB(response);
                const newAddressId = parsedAddress?.id || localCart?.address_id;
                doUpdateCart({
                    ...localCart,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    address_id: newAddressId?.toString(),
                });
            });
    }, [actionTextOFOWidget, doUpdateCart, localCart, selectedAddress, onClickOFOWidgetEvent]);

    const setScrolledState = useCallback(() => {
        setPageScrolled(true);
    }, []);

    const unsetScrolledState = useCallback(() => {
        setPageScrolled(false);
    }, []);

    const handleCloseFreebiePopup = useCallback(() => {
        setShowFreebiePopup?.(false);
    }, [setShowFreebiePopup]);

    /**
     * This hack of passing component as a prop to billdetail is done because
     * product wants to reposition and play around widgets on priority.
     * This would be fixed in PL migration
     */

    const EditNumberWidgetOfo = useMemo(() => {
        return (
            <EditNumberWidget
                title={titleOFOWidget}
                subTitle={subTitleOFOWidget}
                actionText={actionTextOFOWidget}
                showGiftIcon={showGiftIcon}
                onActionClick={onOFOWidgetActionClick}
                onImpression={onImpressionOFOWidgetEvent}
            />
        );
    }, [
        titleOFOWidget,
        subTitleOFOWidget,
        actionTextOFOWidget,
        showGiftIcon,
        onOFOWidgetActionClick,
        onImpressionOFOWidgetEvent,
    ]);

    const renderOfoWidgetAtTop = useMemo(() => {
        if (canShowOFOWidgetAtTop) {
            return (
                <div
                    className={classNames(styles.containerOfoDetailedBanner, {
                        [styles.containerOfoDetailedBanner]: subTitleOFOWidget,
                        [styles.containerOfoBanner]: !subTitleOFOWidget,
                    })}
                >
                    {EditNumberWidgetOfo}
                </div>
            );
        }
        return null;
    }, [canShowOFOWidgetAtTop, subTitleOFOWidget, EditNumberWidgetOfo]);

    const handleIrctcEditAddress = useCallback(
        (ctaType: IrctcAddressChangeFormKeys) => () => {
            onClickIrctcEditCta?.(ctaType);
            getBridgeSdk()
                .getAsyncIrctcPayload()
                .then((payload) => {
                    if (payload?.irctc_data.berth) {
                        doUpdateCart({
                            ...localCart,
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            irctc_data: payload.irctc_data,
                        });
                    }
                });
            const addressChangeUrl = getIrctcAddressChangeUrl();
            getBridgeSdk().navigateToScreen({
                type: NavigationTypes.DEEPLINK,
                link: format(
                    IRCTC_ADDRESS_CHANGE_DEEPLINK,
                    encodeURIComponent(
                        format(addressChangeUrl, ctaType, String(cartResponse?.data?.restaurant_details?.id || "")),
                    ),
                ),
            });
        },
        [cartResponse?.data?.restaurant_details?.id, doUpdateCart, localCart, onClickIrctcEditCta],
    );

    // This is to disable edit address flow in movie beam flow
    const disableAddOrEditAddressFlow = useMemo(() => isMovieBeamContextExists(localCart), [localCart]);

    const handleSelectedAddress = useMemo(() => {
        if (isIrctcCart) {
            return handleIrctcEditAddress(IrctcAddressChangeFormKeys.COACH_NO);
        }
        if (disableAddOrEditAddressFlow) {
            return undefined;
        }
        return goToSelectAddress;
    }, [isIrctcCart, goToSelectAddress, handleIrctcEditAddress, disableAddOrEditAddressFlow]);

    const goToCouponListing = useCallback(
        (couponListingType: CouponPageType) => {
            resetCouponListingPage();

            setCheckoutState({
                type: CheckoutActionTypes.SHOW_COUPON_LISTING,
                payload: {
                    couponListingLaunchTimestamp: Date.now(),
                    couponListingType,
                },
            });
        },
        [resetCouponListingPage, setCheckoutState],
    );

    const renderIrctcAddressChange = useMemo(() => {
        const irctcData = getIrctcIdentifier(cartResponse);
        const trainOrderAttributes = getIrctcTrainOrderAttributes(cartResponse);
        if (showIrctcCart && irctcData) {
            // to use this flag to redirect to irctc homepage when cart is clear on Browse Restaurant CTA.
            setIrctcCartSession(true);
            return (
                <IrctcAddressChange
                    irctcData={irctcData}
                    trainOrderAttributes={trainOrderAttributes}
                    selectedAddress={selectedAddress}
                    editHandler={handleIrctcEditAddress}
                    onImpressionIrctcUpdateAddress={onImpressionIrctcUpdateAddress}
                    hideEditCta={isIxigoFlow()}
                />
            );
        }
        return null;
    }, [
        cartResponse,
        handleIrctcEditAddress,
        onImpressionIrctcUpdateAddress,
        setIrctcCartSession,
        showIrctcCart,
        selectedAddress,
    ]);

    const isIncognitoCart = useMemo(() => cartResponse?.data?.is_incognito_cart, [cartResponse]);

    const incognitoModeToggleHandler = useCallback(
        (isEnabled: boolean) => {
            doUpdateCart({ ...localCart, is_incognito_cart: isEnabled });
        },
        [doUpdateCart, localCart],
    );

    const show429ErrorScreen = useMemo(() => {
        return (
            // this is for when cart loads and we make the 1st api call
            // this is for when we update cart after load and it fails
            (!loading &&
                !isEmpty(cartApiError) &&
                cartApiError.httpStatusCode == HTTP_STATUS_CODES.TOO_MANY_REQUESTS) ||
            (!updateCartInProgress && updateCartError?.httpStatusCode === HTTP_STATUS_CODES.TOO_MANY_REQUESTS)
        );
    }, [cartApiError, loading, updateCartError?.httpStatusCode, updateCartInProgress]);

    const showNetworkConnectivityScreen = useMemo(() => {
        return (
            // this is for when cart loads and we make the 1st api call
            !loading && cartApiError?.errorPayload && isNetworkError(cartApiError?.errorPayload)
        );
    }, [cartApiError, loading]);

    useEffect(() => {
        if (checkoutState.type !== CheckoutActionTypes.SHOW_CART) {
            return;
        }

        if (turboPaymentEnabled) {
            const cartId = cartResponse?.data?.cartId || "";
            const addressId = cartResponse?.data?.address_id || "";

            syncCartAddressHeaders({ cartId, addressId });
        }
    }, [cartResponse?.data?.address_id, cartResponse?.data?.cartId, turboPaymentEnabled, checkoutState]);

    useEffect(() => {
        if (cartResponse && cartResponse?.data) {
            const cartId = cartResponse?.data?.cartId || "";
            onWebcartUpdateImpression(impressionEvent, {
                cartId,
                restaurantId: getRestaurantId(cartResponse),
                cartTotal: cartResponse?.data.cart_total,
                couponCode: cartResponse?.data?.coupon_code,
                couponApplied: !!cartState.localCart?.isCouponValid,
                turboPaymentEnabled,
                xpValues: JSON.stringify(cartResponse?.data.XPData),
                groupCartId: groupCartState?.data?.group_cart_id,
            });
        }
    }, [
        cartResponse,
        cartResponse?.data,
        cartState.localCart?.isCouponValid,
        groupCartState.data?.group_cart_id,
        impressionEvent,
        isGroupCartFlow,
        turboPaymentEnabled,
    ]);

    const handleViewEnter = useCallback(() => {
        impressionCancellationPolicyWidget({ impressionEvent, cartId: cartResponse?.data?.cartId });
    }, [impressionEvent, cartResponse?.data?.cartId]);

    const isEmptyCart = useMemo(() => cartResponse?.data && isCartEmpty(cartResponse.data), [cartResponse?.data]);

    useEffect(() => {
        if (isEmptyCart) {
            // Auto redirect when cart is empty
            getBridgeSdk().clearLocalCart({
                tenant: {
                    marketplaceBusinessline: "Regular",
                    marketplaceCategory: "Food",
                    marketplaceId: 1,
                },
            });

            handleEmptyCartOnLoad();
        }
    }, [isEmptyCart]);

    if (showNetworkConnectivityScreen) {
        return (
            <CartUIWrapper disableRedirectionToMenu={disableRedirectionToMenu} headerClass={styles.headerError}>
                <GenericError
                    action={{
                        text: RETRY,
                        onClick: () => {
                            window.location.reload();
                        },
                    }}
                />
            </CartUIWrapper>
        );
    }

    if (show429ErrorScreen) {
        return (
            <CartUIWrapper disableRedirectionToMenu={disableRedirectionToMenu} headerClass={styles.headerError}>
                <GenericError
                    pageImpressionEvent={onImpressionTooManyRequestsErrorPage}
                    action={null}
                    primaryAction={{
                        timeDisabledFor: ERROR_SCREEN_RETRY_WAIT_TIME_SECS,
                        text: RETRY,
                        onClick: () => {
                            window.location.reload();
                        },
                    }}
                />
            </CartUIWrapper>
        );
    }

    if (!cartResponse || loading) {
        return (
            <CartUIWrapper disableRedirectionToMenu={disableRedirectionToMenu}>
                <Shimmer />
            </CartUIWrapper>
        );
    }

    const isSessionExpiredError = cartResponse.statusCode === CART_STATUS_CODES.SESSION_EXPIRED;
    if (!cartResponse.data || cartResponse.statusCode === CART_STATUS_CODES.FAILURE || isSessionExpiredError) {
        const ctaAction = isSessionExpiredError ? CtaAction.GO_TO_LOGIN : cartResponse.ctaAction;
        const ctaText = isSessionExpiredError ? LOGIN : cartResponse.ctaText;
        return (
            <CartUIWrapper disableRedirectionToMenu={disableRedirectionToMenu} headerClass={styles.headerError}>
                {cartResponse !== null ? (
                    <CartError
                        titleMessage={cartResponse.titleMessage}
                        statusMessage={cartResponse.statusMessage}
                        stayOnCartScreen={cartResponse.stayOnCartScreen}
                        goToHome={goToHome}
                        goBack={closeWebview}
                        ctaAction={ctaAction}
                        ctaText={ctaText}
                    />
                ) : (
                    <GenericError
                        pageImpressionEvent={onImpressionNullCartResponseErrorPage}
                        action={{ onClick: reloadCart }}
                    />
                )}
            </CartUIWrapper>
        );
    }

    if (isEmptyCart) {
        if (!isJioHotstarFlow()) {
            return <></>;
        }

        return (
            <div className={styles.emptyCartContainer}>
                <Loader type="primary" size="large" />
            </div>
        );
    }

    const couponErrorData = getCouponErrorCard(cartResponse.data);

    const isNewPORFlow = isPORV2Flow(porFlowXP);

    const shouldShowExtraPaddingUnservicableRx =
        !isCartStatusSuccess || !showAddressWidgetTop || selectedAddress?.delivery_valid !== 1;

    const showExtraPaddingBottom = shouldShowExtraPaddingUnservicableRx || forceExtraPaddingOnCart;

    const containerClassName = classNames(styles.bottomPadding, {
        [styles.topPadding]: isEmpty(savingsBannerData) && !canShowOFOWidgetAtTop && !showAddressWidgetTop,
        [styles.bottomPaddingExtra]: showExtraPaddingBottom,
    });

    const headerClassName = classNames({
        [styles.header]: isEmpty(savingsBannerData) && !canShowOFOWidgetAtTop,
        [styles.headerV2]: true,
        [styles.headerScrolled]: isPageScrolled,
    });

    return (
        <CartUIWrapper
            disableRedirectionToMenu={disableRedirectionToMenu}
            containerId={CART_ELEMENT_IDS.CART_RENDERER_CONTAINER_ID}
            containerClass={containerClassName}
            headerClass={headerClassName}
            showCartFeedback={cartFeedbackXP}
            statusCode={cartResponse.statusCode}
            restaurantName={getRestaurantName(cartResponse)}
            cartId={getCartId(cartResponse)}
            restaurantId={getRestaurantId(cartResponse)}
            itemCount={getCartItemsCount(cartResponse)}
            isPageScrolled
            selectedAddress={headerSelectedAddress}
            onAddressDropdownClick={handleSelectedAddress}
            qFoodSLA={qFoodSLA}
            showAddressWidgetTop={!!showAddressWidgetTop}
            irctcData={getIrctcIdentifier(cartResponse)}
            isIrctcCart={isIrctcCartSession}
            showGreenSubtitle={isOnTimeCart(cartResponse?.data?.promises)}
            incognitoModeToggleHandler={incognitoModeToggleHandler}
            cartIncognitoModeXp={!isMweb}
            isIncognitoCart={isIncognitoCart || false}
            isSavingsBannerExist={!isEmpty(savingsBannerData)}
            goToHome={goToHome}
            useHeaderV2={true}
        >
            <ElementObserver
                onViewEnter={unsetScrolledState}
                observerClassName={styles.observerNoWidth}
                onViewExit={setScrolledState}
            >
                <div className={styles.marginBottomDiv} />
            </ElementObserver>
            {renderOfoWidgetAtTop}
            {renderIrctcAddressChange}
            {/* <ScreenReaderOnly as="button" text={ACCESSIBLITY_TEXTS.SKIP_TO_PAYMENT} onClick={focusMakePaymentButton} /> */}
            {(showCouponModal || showTradeDiscountCoupon) && (
                <CouponModalWrapper
                    couponErrorData={couponErrorData}
                    couponSuccessData={couponSuccessData}
                    closeModal={handleCartModalClose}
                    onClickCouponSavingsPopup={onClickCouponSavingsPopup}
                    onClickCouponSavingsPopupWithPOR={onClickCouponSavingsPopupWithPOR}
                    triggerConfettiByType={triggerCouponConfetti}
                    isCouponAutoApplied={isCouponAutoApplied}
                    impressionAutoApplyCouponPopup={impressionAutoApplyCouponPopup}
                    impressionOffercashSuccessPopup={impressionOffercashSuccessPopup}
                    showCouponConfetti={showCouponConfetti}
                    tradeDiscountCouponInfo={tradeDiscountCouponInfo}
                    onClickTDCouponModal={onClickTDCouponModal}
                    isNewPORFlow={isNewPORFlow}
                    goToCouponListing={goToCouponListing}
                    onImpressionMoreOffersStrip={onImpressionMoreOffersStrip}
                    onClickMoreOffersStrip={onClickMoreOffersStrip}
                    onClickCouponMaxSavingsPopup={onClickCouponMaxSavingsPopup}
                />
            )}

            {showFreebiePopup && !isEmpty(freebiePopupInfo) && (
                <FreebiePopup
                    data={freebiePopupInfo}
                    closeModal={handleCloseFreebiePopup}
                    showCouponConfetti={showCouponConfetti}
                    triggerConfettiByType={triggerCouponConfetti}
                />
            )}
            {showDelightPopup && tradeDiscountSuccessInfo && (
                <DelightPopup
                    data={tradeDiscountSuccessInfo}
                    closeModal={handleCloseDelightPopup}
                    onImpressionDelightPopup={impressionDelightPopup}
                    onClickCloseDelightPopup={onClickCloseDelightPopup}
                    isJioHotstarFlow={isJioHotstarFlow()}
                />
            )}
            {showBharatMandapamConfirm && (
                <BharatMandapamConfirmPickup
                    isOpen={showBharatMandapamConfirm}
                    onClose={handleCloseBharatMandapamConfirm}
                    onConfirm={handleCloseBharatMandapamConfirm}
                    onChangePickupPoint={handleBharatMandapamChangePickup}
                    selectedAddress={selectedAddress}
                />
            )}
            <CartWidgetRenderer
                canShowTimer={canShowTimer}
                setCroutonVisibility={setCroutonVisibility}
                canShowOFOWidgetAtTop={canShowOFOWidgetAtTop}
                planBullsEyeTGProps={planBullsEyeTGProps}
                planAddedRef={planAddedRef}
                goToMenu={goToMenu}
                isCroutonVisible={isCroutonVisible}
                isValueMeal={isValueMeal}
            />
            {/* <ScreenReaderOnly as="button" text={ACCESSIBLITY_TEXTS.SKIP_TO_PAYMENT} onClick={focusMakePaymentButton} /> */}
            {canShowTimer && (
                <CartTimerWrapper
                    source={Source.CART}
                    onComplete={onTimerEnd}
                    onTimerLoad={onTimerLoad}
                    setCroutonVisibility={setCroutonVisibility}
                />
            )}
            {showCartToast && <GenericCartToast toastData={cartToastData} />}
            {!isIrctcCartSession && (
                <CancellationPolicyWidget
                    cancellationDisclaimerData={cartResponse?.data?.cancellation_policy_widget}
                    onViewEnter={handleViewEnter}
                />
            )}

            <CartFooter
                addressId={cartResponse.data.address_id}
                addresses={cartResponse.data.addresses}
                cartTotal={cartResponse.data.cart_total}
                tipAmount={getTipAmount(cartResponse)}
                crouton={cartResponse.data.crouton}
                canShowCrouton={canShowFooterCrouton}
                statusCode={cartResponse.statusCode}
                statusMessage={cartResponse.statusMessage}
                titleMessage={cartResponse.titleMessage}
                onMakePayment={onMakePayment}
                ctaText={cartResponse.ctaText}
                ctaAction={cartResponse.ctaAction}
                viewDetailedBill={viewDetailedBill}
                cartItems={getMenuItems(cartResponse)}
                unServiceableItemsCount={getUnServiceableItemsCount(cartResponse)}
                canPlaceOrderFromCart={canPlaceOrderFromCart}
                makePaymentButtonRef={makePaymentButtonRef}
                qFoodSLA={qFoodSLA}
                deliverySlots={deliverySlots}
                restaurantId={cartResponse.data.restaurant_details?.id}
                cartId={cartResponse.data.cartId}
                isTurboPayTooltipHidden={isCroutonVisible}
                setCroutonVisibility={setCroutonVisibility}
                isTurboPaymentEnabled={turboPaymentEnabled}
                setIsTurboPayWidgetHidden={setIsTurboPayWidgetHidden}
                updateCartInProgress={updateCartInProgress}
                backgroundRefreshInProgress={backgroundRefreshInProgress}
                isBulkOrder={isBulkOrderFn}
                irctcData={getIrctcIdentifier(cartResponse)}
                setForceExtraPaddingOnCart={setForceExtraPaddingOnCart}
            />
            <AddressListManager reviewedCart={cartResponse} displayAddressList={displayAddressList} />
            {(updateCartInProgress || backgroundRefreshInProgress) && <UpdateProgressLoader />}
            {planBullsEyeTGProps.ShouldBullsEyeTGCardMount && (
                <SwiggyPlanBullsEyeTGWrapper
                    {...planBullsEyeTGProps}
                    showConfetti={showConfetti}
                    planAddedRef={planAddedRef}
                    showDelightPopup={showDelightPopup}
                />
            )}
            <BlackMembershipNudgesWrapper />
            <GenericCroutonWrapper />
            {showM2ChalfcardOnClick?.status && (
                <M2CHalfcardContainer ignoreCapping={true} showDelightPopup={showDelightPopup} />
            )}
            {showCartSplashLottie && <SplashLottie onImpression={onImpressionSplashLottie} />}
        </CartUIWrapper>
    );
}

export default memo(CartRenderer);
